import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BookmarkPlus, Bookmark, Trash2, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/creatives`;

export const TemplatesMenu = ({ refreshKey, onApply }) => {
  const [items, setItems] = useState([]);
  const load = () => axios.get(`${API}/templates`).then((r) => setItems(r.data.items || [])).catch(() => {});
  useEffect(() => { load(); }, [refreshKey]);
  const del = async (t) => { if (!window.confirm(`Ștergi șablonul „${t.name}”?`)) return; await axios.delete(`${API}/templates/${t.id}`); load(); };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid="templates-btn" className="flex items-center gap-1.5 rounded-full border border-zinc-700 px-3 py-0.5 text-xs text-zinc-300 hover:border-amber-400">
          <Bookmark className="h-3.5 w-3.5 text-amber-400" /> Șabloane{items.length ? ` (${items.length})` : ""}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-2" data-testid="templates-list">
        {items.length === 0 && <p className="p-2 text-xs text-zinc-500" data-testid="templates-empty">Niciun șablon. Apasă „Salvează șablon” sub o imagine reușită.</p>}
        {items.map((t) => (
          <div key={t.id} className="group flex items-start gap-2 rounded-md p-2 hover:bg-zinc-100" data-testid={`template-${t.id}`}>
            <button className="flex-1 text-left" onClick={() => onApply(t)} data-testid={`template-apply-${t.id}`}>
              <span className="block text-xs font-semibold text-zinc-900">{t.name} <span className="font-normal text-zinc-500">· {t.aspect_ratio}</span></span>
              <span className="line-clamp-2 text-[11px] text-zinc-600">{t.text}</span>
            </button>
            <button onClick={() => del(t)} data-testid={`template-delete-${t.id}`} className="p-1 text-zinc-500 hover:text-rose-400"><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        ))}
      </PopoverContent>
    </Popover>
  );
};

export const SaveTemplateDialog = ({ draft, onClose, onSaved }) => {
  const [name, setName] = useState("");
  const [text, setText] = useState(draft.text);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await axios.post(`${API}/templates`, { ...draft, name, text }); toast.success("Șablon salvat"); onSaved(); onClose(); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="save-template-dialog" className="max-w-md">
        <DialogHeader><DialogTitle>Salvează ca șablon</DialogTitle></DialogHeader>
        <p className="text-xs text-zinc-500">Instrucțiunile de mai jos se refolosesc pe orice alt produs. Scoate ce ține doar de acest produs (ex. titlul sau prețul exact).</p>
        <Input data-testid="template-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nume șablon, ex. Bucătărie caldă" maxLength={60} />
        <Textarea data-testid="template-text-input" value={text} onChange={(e) => setText(e.target.value)} rows={5} />
        <button data-testid="template-save-btn" onClick={save} disabled={busy || !name.trim() || !text.trim()}
          className="flex items-center justify-center gap-2 rounded-full bg-amber-400 py-2.5 text-sm font-semibold text-zinc-950 disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <BookmarkPlus className="h-4 w-4" />} Salvează
        </button>
      </DialogContent>
    </Dialog>
  );
};
